import { EllipsisSpan, Tag } from '@maxtropy/components';
import { ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';
import { AlarmLevelDisplay } from '@/pages/PvAlarm/types';
import { useEffect, useState } from 'react';
import {
  apiV2DeviationRateAnalysisGetAllEnergyMediumTopologyPost,
  apiV2DeviationRateAnalysisGetEnergyMediumsPost,
  apiV2DeviationRateAnalysisGetEnergyMediumTopologyNodePost,
  V2DeviationRateAnalysisPagePostResponse,
} from '@maxtropy/device-customer-apis-v2';

// 列表项类型 - 从API响应中提取
export type DeviationAlarmListItem = NonNullable<V2DeviationRateAnalysisPagePostResponse['list']>[number];

// 状态枚举
export enum DeviationAlarmStatus {
  Disable = 0,
  Enable = 1,
}

export const DeviationAlarmStatusDisplay = {
  [DeviationAlarmStatus.Disable]: '禁用',
  [DeviationAlarmStatus.Enable]: '启用',
};

// 选项Map类型
export type OptionsMap = {
  energyMediumTopologyId?: Array<{ label: string; value: string | number }>;
  topologyNodeIds?: Array<{ label: string; value: string | number }>;
  energyMediums?: Array<{ label: string; value: string | number }>;
  ruleNames?: Array<{ label: string; value: string | number }>;
  status?: Array<{ label: string; value: number }>;
  alarmLevel?: Array<{ label: string; value: number }>;
};

export const useDeviationOptions = () => {
  // 选项集合
  const [optionsMap, setOptionsMap] = useState<OptionsMap>({});
  const [cascadeOptions, setCascadeOptions] = useState<OptionsMap>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOptions = async () => {
      const settleToOptions = (
        result: PromiseSettledResult<{
          list?: Array<{ id?: string | number; name?: string }>;
        }>
      ) => {
        return result.status === 'fulfilled' ? listToOptions(result.value.list) : [];
      };

      const results = await Promise.allSettled([
        // 能源介质拓扑
        apiV2DeviationRateAnalysisGetAllEnergyMediumTopologyPost({}),
        // 拓扑节点
        apiV2DeviationRateAnalysisGetEnergyMediumTopologyNodePost({}),
        // 能源介质
        apiV2DeviationRateAnalysisGetEnergyMediumsPost({}),
      ]);

      setOptionsMap({
        energyMediumTopologyId: settleToOptions(results[0]),
        topologyNodeIds: settleToOptions(results[1]),
        energyMediums: settleToOptions(results[2]),
        status: displayObjToOptions(DeviationAlarmStatusDisplay),
        alarmLevel: displayObjToOptions(AlarmLevelDisplay),
      });
      setLoading(false);
    };

    fetchOptions();
  }, []);

  // 获取级联选项
  const fetchCascadeOptions = async <T extends { list?: any[] }>(
    field: keyof OptionsMap,
    fetchFn: () => Promise<T>
  ) => {
    try {
      const res = await fetchFn();
      setCascadeOptions(prev => ({
        ...prev,
        [field]: listToOptions(res.list),
      }));
    } catch (error) {
      setCascadeOptions(prev => ({ ...prev, [field]: [] }));
    }
  };

  return {
    loading,
    optionsMap: { ...optionsMap, ...cascadeOptions },
    fetchCascadeOptions,
  };
};

// 工具函数：列表数据转为 options
export const listToOptions = <T extends { id?: string | number; name?: string }>(
  list: T[] = []
): Array<{ label: string; value: string | number }> => {
  return list
    .filter(item => item.id !== undefined && item.name !== undefined)
    .map(item => ({ label: item.name!, value: item.id! }));
};

// 工具函数：枚举 Display 对象转为 options
export const displayObjToOptions = (enumObj: Record<number, string>) => {
  return Object.entries(enumObj).map(([key, value]) => ({
    label: value,
    value: Number(key),
  }));
};

export const getTableListColumns = (): ColumnType<DeviationAlarmListItem>[] => [
  {
    title: '规则名称',
    dataIndex: 'ruleName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '能源介质拓扑',
    dataIndex: 'energyMediumTopologyName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '拓扑节点',
    dataIndex: 'topologyNodes',
    ellipsis: { showTitle: true },
    render: (v: { id?: number; nodeName?: string }[] | undefined) => (
      <EllipsisSpan value={v ? v.map(i => i.nodeName).join('、') : '-'} />
    ),
  },
  {
    title: '能源介质',
    dataIndex: 'energyMediumName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '状态',
    dataIndex: 'status',
    ellipsis: { showTitle: true },
    render: (v: number) => (
      <Tag border="solid" color={v === DeviationAlarmStatus.Enable ? 'success' : 'default'}>
        {DeviationAlarmStatusDisplay[v as DeviationAlarmStatus]}
      </Tag>
    ),
  },
  {
    title: '规则编号',
    dataIndex: 'ruleCode',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '最后操作时间',
    dataIndex: 'updateTime',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-'} />,
  },
  {
    title: '最后操作人',
    dataIndex: 'updateUser',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v || '-'} />,
  },
];

// 按天, 按小时
export const CycleType = {
  date: 'DAY',
  hour: 'HOUR',
} as const;

export type CycleType = typeof CycleType[keyof typeof CycleType];
